import Layout from '@/components/Layout'
import { useState, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faSearch } from '@fortawesome/free-solid-svg-icons'
import Pagination from '@/components/Pagination'
import api from '../../services/api'
import { API_ENDPOINTS } from '../../config/api'
import '../reports/style.css'

/**
 * AccountStatement Component
 * 
 * Page for viewing account statements with filters, tabs, and export options.
 */
function AccountStatementDetails() {
  const [entriesPerPage, setEntriesPerPage] = useState(50)
  const [searchValue, setSearchValue] = useState('')
  const [balanceType, setBalanceType] = useState('All')
  const [userSearch, setUserSearch] = useState('')
  const [fromDate, setFromDate] = useState('2025-12-18')
  const [toDate, setToDate] = useState('2025-12-25')
  const [activeTab, setActiveTab] = useState('P&L Statement') // 'P&L Statement' or 'Credit History'
  const [plStatementData, setPlStatementData] = useState([])
  const [creditHistoryData, setCreditHistoryData] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [showIp, setShowIp] = useState({}) // Track which IPs are visible
  const [showFromTo, setShowFromTo] = useState({}) // Track which FromTo are visible
  const [totalRecords, setTotalRecords] = useState(0)
  const [recommendations, setRecommendations] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)

  // Sample data matching the image


  // Get current table data based on active tab
  const tableData = activeTab === 'P&L Statement' ? plStatementData : creditHistoryData

  // With server-side pagination, the loaded data IS the paginated data
  const paginatedData = tableData
  const totalPages = Math.max(1, Math.ceil(totalRecords / entriesPerPage))

  // Handle Load button click
  const fetchStatement = async () => {
    setLoading(true)
    try {
      const response = await api.get(API_ENDPOINTS.WALLET_STATEMENT, {
        params: {
          from_date: fromDate,
          to_date: toDate,
          limit: entriesPerPage,
          page: currentPage,
          search: searchValue,
          type: balanceType,
          username: userSearch
        }
      })

      if (response.data && response.data.data) {
        const transactions = Array.isArray(response.data.data) ? response.data.data : []
        const total = response.data.total || response.data.meta?.total || transactions.length // Fallback
        setTotalRecords(total)

        // Map API data to component state structure
        const mappedData = transactions.map((item, index) => {
          // Robust checking for user fields
          const fromUser = item.username || item.user?.username || item.sender?.username || item.from_user?.username || '';
          const toUser = item.counterparty || item.receiver?.username || item.to_user?.username || '';

          return {
            id: item.id || index,
            date: item.created_at || item.date,
            description: item.description || item.remark || item.type,
            ip: item.ip_address || '',
            fromTo: `${fromUser}/${toUser}`,
            amount: parseFloat(item.amount) || 0,
            closing: parseFloat(item.closing_balance) || 0,
            userName: fromUser,
            masterName: toUser,
            remark: item.remark || '',
            stake: parseFloat(item.amount) || 0
          };
        });

        if (activeTab === 'P&L Statement') {
          setPlStatementData(mappedData)
        } else {
          // Filter specifically for "By Credit" remark as requested
          const creditData = mappedData.filter(item => item.remark === 'By Credit')
          setCreditHistoryData(creditData)
        }
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLoad = () => {
    setCurrentPage(1)
    fetchStatement()
  }

  useEffect(() => {
    fetchStatement()
  }, [currentPage, entriesPerPage, activeTab, balanceType]) // Trigger on balanceType change too

  // Debounced User Search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearch.length >= 3) {
        fetchUserSuggestions(userSearch)
      } else {
        setRecommendations([])
        setShowDropdown(false)
      }
    }, 2000)

    return () => clearTimeout(timer)
  }, [userSearch])

  const fetchUserSuggestions = async (searchTerm) => {
    setSearchLoading(true)
    setShowDropdown(true)
    setRecommendations([]) // Clear previous results while searching
    try {
      const response = await api.get(API_ENDPOINTS.SEARCH_BY_USERNAME, {
        params: { username: searchTerm }
      })
      if (response.data?.data?.results && Array.isArray(response.data.data.results)) {
        setRecommendations(response.data.data.results)
      } else {
        setRecommendations([])
      }
    } catch (error) {
      console.error('Error fetching user suggestions:', error)
      setRecommendations([])
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSelectUser = (username) => {
    setUserSearch(username)
    setShowDropdown(false)
    setRecommendations([])
  }


  // Handle Download CSV
  const handleDownloadCSV = () => {
    console.log('Downloading CSV...')
    // TODO: Implement CSV download
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const toggleShowIp = (id) => {
    setShowIp(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const toggleShowFromTo = (id) => {
    setShowFromTo(prev => ({ ...prev, [id]: !prev[id] }))
  }

  return (

      <div className=" w-full mx-auto bg-[#0f1224]   h-auto">
        {/* Header Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-2 pt-4"
        >

        </div>

        {/* Filter Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-10px" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">Balance Type</label>
              <select
                value={balanceType}
                onChange={(e) => setBalanceType(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none min-w-[150px]"
              >
                <option value="All">All</option>
                <option value="Credit">Credit</option>
                <option value="Debit">Debit</option>
              </select>
            </div>

            <div className="flex flex-col relative">
              <label className="text-white text-[12px] font-bold mb-1">Search by user</label>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => {
                    setUserSearch(e.target.value)
                    setShowDropdown(false) // Hide dropdown while typing until debounce fires
                }}
                placeholder="Enter Atleast 3 character"
                className="px-2 py-2 text-[11px] bg-white text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
              />
              {showDropdown && (
                <ul className="absolute top-full left-0 w-full bg-white text-black text-[12px] rounded-sm shadow-lg max-h-40 overflow-y-auto z-50">
                  {searchLoading ? (
                    <li className="px-2 py-2 text-gray-500 italic">Searching...</li>
                  ) : recommendations.length > 0 ? (
                    recommendations.map((user, index) => (
                      <li
                        key={index}
                        onClick={() => handleSelectUser(user.username)}
                        className="px-2 py-2 cursor-pointer hover:bg-blue-100"
                      >
                        {user.username}
                      </li>
                    ))
                  ) : (
                     <li className="px-2 py-2 text-gray-500 italic">No record found</li>
                  )}
                </ul>
              )}
            </div>

            <div style={{ width: "16%" }} className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
              />
            </div>

            <div style={{ width: "16%" }} className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
              />
            </div>

            <button
              onClick={handleLoad}
              disabled={loading}
              className="px-6 py-2 bg-[#11374a] text-white font-medium text-[12px] rounded-sm hover:bg-[#2d3569] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon className="mr-2" icon={faSearch} />
              Search
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex mb-4">
            <button
              onClick={() => {
                setActiveTab('P&L Statement')
                setCurrentPage(1)
              }}
              className={`px-4 py-2 text-[12px] font-bold transition-colors ${activeTab === 'P&L Statement'
                ? 'bg-[#333d79ff] text-white'
                : 'bg-gray-600 text-white'
                }`}
            >
              P&L Statement
            </button>
            <button
              onClick={() => {
                setActiveTab('Credit History')
                setCurrentPage(1)
              }}
              className={`px-4 py-2 text-[12px] font-bold transition-colors ${activeTab === 'Credit History'
                ? 'bg-[#333d79ff] text-white'
                : 'bg-gray-600 text-white'
                }`}
            >
              Credit Statement
            </button>
          </div>

        
        </div>
           
        <div className="w-full h-px bg-white px-1 mb-2"></div>

        {/* Table Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {activeTab === 'P&L Statement' ? (
                    <>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Date
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Description
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Ip
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        From To
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Amount
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Closing
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Date
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        User Name
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Agent Name
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Remark
                      </th>
                      <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                        Stake
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeTab === 'P&L Statement' ? 6 : 5} className="px-2 py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'P&L Statement' ? 6 : 5} className="px-2 py-8 text-center text-gray-400">
                      There are no records to show
                    </td>
                  </tr>
                ) : activeTab === 'P&L Statement' ? (
                  paginatedData.map((row, index) => (
                    <tr
                      key={row.id}
                      className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                        }`}
                    >
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.date || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.description || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.ip ? (
                          <div className="flex items-center gap-1">
                            {showIp[row.id] ? (
                              <>
                                <span>{row.ip}</span>
                                <button
                                  onClick={() => toggleShowIp(row.id)}
                                  className="text-white hover:text-blue-300 transition-colors"
                                >
                                  <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => toggleShowIp(row.id)}
                                className="text-white hover:text-blue-300 transition-colors"
                              >
                                <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => toggleShowIp(row.id)}
                            className="text-white hover:text-blue-300 transition-colors"
                          >
                            <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                          </button>
                        )}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.fromTo || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.amount !== undefined ? row.amount.toFixed(2) : '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.closing !== undefined ? row.closing.toFixed(2) : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  paginatedData.map((row, index) => (
                    <tr
                      key={row.id}
                      className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                        }`}
                    >
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.date || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.userName || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.masterName || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.remark || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.stake !== undefined ? row.stake.toFixed(2) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

  )
}

export default AccountStatementDetails;
